// Drawer tab strip shared by request-workspace surfaces. The strip
// renders a `role="tablist"` with one `role="tab"` button per descriptor.
// On narrow viewports a tab collapses to its `icon` plus an optional
// bare `count` integer (no parentheses); the accessible name stays the
// localized desktop `label` so screen-reader queries keep matching. See
// `apps/web/docs/request-workspace-ui-contract.md`.

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from './Icon.js';

const NOTIFICATION_DISPLAY_CAP = 99;

export interface WorkspaceTabDescriptor<TId extends string = string> {
  id: TId;
  /** Localized text label rendered on desktop and used as the accessible name on narrow. */
  label: string;
  /** Icon name shown in narrow mode. Required to enable the icon-collapse rule. */
  icon?: IconName;
  /** Optional integer count rendered next to the icon on narrow. */
  count?: number | null;
  /**
   * Optional unread-notification count (#1072). When > 0 a small amber
   * dot/number renders alongside the tab text. Zero/undefined hides it.
   * Always shown — both on desktop and on narrow — so users on either
   * breakpoint can see which tab carries pending notifications.
   */
  notificationCount?: number;
  /** Value for the tab button's `aria-controls`. */
  controls?: string;
  /** Value for the tab button's `id`. */
  buttonId?: string;
  /** Optional `data-testid` on the tab button. */
  testId?: string;
}

export interface WorkspaceTabStripProps<TId extends string = string> {
  tabs: readonly WorkspaceTabDescriptor<TId>[];
  activeId: TId;
  onSelect: (id: TId) => void;
  narrow: boolean;
  /** CSS `padding` on the tablist container. Defaults to China's `0 24px`. */
  padding?: string;
  /** Optional `data-testid` on the tablist container. */
  testId?: string;
}

const STRIP_BASE: CSSProperties = {
  display: 'flex',
  gap: 0,
  marginTop: 4,
  borderBottom: '1px solid var(--border-1)',
  flex: 'none',
  minWidth: 0,
};

const NARROW_STRIP: CSSProperties = { overflowX: 'auto', maxWidth: '100%' };

const BUTTON_BASE: CSSProperties = {
  padding: '10px 14px',
  fontSize: 13,
  fontWeight: 500,
  marginBottom: -1,
  background: 'transparent',
  cursor: 'pointer',
  border: 'none',
};

const NARROW_BUTTON: CSSProperties = {
  flex: '1 1 0',
  minWidth: 0,
  padding: '10px 8px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
};

export function WorkspaceTabStrip<TId extends string>({
  tabs,
  activeId,
  onSelect,
  narrow,
  padding = '0 24px',
  testId,
}: WorkspaceTabStripProps<TId>) {
  const { t: translate } = useTranslation('shell');
  return (
    <div
      role="tablist"
      data-testid={testId}
      style={{
        ...STRIP_BASE,
        padding,
        ...(narrow ? NARROW_STRIP : null),
      }}
    >
      {tabs.map((t) => {
        const selected = t.id === activeId;
        const buttonStyle: CSSProperties = {
          ...BUTTON_BASE,
          color: selected ? 'var(--fg-1)' : 'var(--fg-3)',
          borderBottom: `2px solid ${selected ? 'var(--accent-1)' : 'transparent'}`,
          ...(narrow ? NARROW_BUTTON : null),
        };
        const showIcon = narrow && t.icon != null;
        const notificationCount = t.notificationCount ?? 0;
        const showNotification = notificationCount > 0;
        const capped = notificationCount > NOTIFICATION_DISPLAY_CAP;
        const notificationLabel = capped
          ? `${NOTIFICATION_DISPLAY_CAP}+`
          : String(notificationCount);
        const notificationAriaLabel = capped
          ? translate('workspaceTab.aria.notifications.countCapped', {
              count: NOTIFICATION_DISPLAY_CAP,
            })
          : translate('workspaceTab.aria.notifications.count', { count: notificationCount });
        const notificationDot = showNotification ? (
          <span
            data-testid={t.testId ? `${t.testId}-notification` : undefined}
            aria-label={notificationAriaLabel}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 16,
              height: 16,
              padding: notificationCount > 9 ? '0 5px' : 0,
              borderRadius: 'var(--r-full)',
              background: 'var(--status-warn-fg)',
              color: 'var(--fg-invert)',
              fontSize: 10,
              fontWeight: 600,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              marginLeft: 6,
            }}
          >
            {notificationLabel}
          </span>
        ) : null;
        return (
          <button
            key={t.id}
            id={t.buttonId}
            data-testid={t.testId}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={t.controls}
            aria-label={showIcon ? t.label : undefined}
            onClick={() => onSelect(t.id)}
            style={buttonStyle}
          >
            {showIcon ? (
              <>
                <Icon name={t.icon!} size={16} aria-hidden />
                {t.count != null && <span>{t.count}</span>}
                {notificationDot}
              </>
            ) : (
              <>
                {t.label}
                {notificationDot}
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
